import {useState, useEffect, Dispatch} from 'react';
import {useHistory} from 'react-router-dom';
import {deleteGroupById, updateGroupById} from '../../api/groups';
import ConfirmationPopup from '../common/popup/ConfirmationPopup';
import {Button, ButtonWithLoader} from '../common/Button';
import {sysAdminGroupSlug} from '../../constants/constants';
import {Form, Formik, FormikHelpers} from 'formik';
import {setTitle} from '../../store/header/header.slice';
import {useAppDispatch} from '../../store/hooks';
import {sanitize} from '../../helpers';
import {handleApiValidationErrors} from '../../helpers/error';
import RequestError from '../../api/RequestError';
import {EditGroupProps, GroupListProps} from '../../constants/types';
import EnsurePermission from '../common/EnsurePermission';
import permissionsInfo from '../../constants/permissionsInfo';
import routes from './routes';
import {editGroupValidationSchema} from '../../constants/validationSchemes';
import useOnUnsavedChanges from '../../hooks/useOnUnsavedChanges';
import {useTranslation} from 'react-i18next';
import {FormGroup} from '@mui/material';
import {TextField} from '../common/formik/fields';
import {useSnackbarHook} from '../../hooks/useSnackbar';

const popupLabels = {
  title: 'Please confirm your action.',
  body: 'The group will be removed from the following tenants:',
  confirmBtn: 'Confirm',
  cancelBtn: 'Cancel'
};

type GroupEditInformationProps = {
  id: string,
  apiData: GroupListProps,
  setGroupName: Dispatch<string>
}

const Information = (
    {id, apiData, setGroupName}: GroupEditInformationProps
) => {
  const {t} = useTranslation('groups');
  const [apiValidationError, setApiValidationError] =
       useState<Nullable<Dictionary<string[]>>>(null);

  const [isPopupShown, setIsPopupShown] = useState(false);

  const clearMessages = () => {
    setApiValidationError(null);
  };

  const initialValues = {name: apiData?.name};
  const dispatch = useAppDispatch();
  const history = useHistory();
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  useEffect(() => {
    dispatch(setTitle(''));
  }, []);

  const onSubmit = async (
      values: EditGroupProps,
      actions: FormikHelpers<EditGroupProps>
  ) => {
    const sanitizeValues = sanitize(values);

    try {
      await updateGroupById(id, sanitizeValues);
      setGroupName(sanitizeValues?.name);
      actions.resetForm({values: sanitizeValues});
      addSuccessToast(t('toasts.updateGroupMessage'));
    } catch (error) {
      if (error instanceof RequestError && error?.validationErrors) {
        const errors = handleApiValidationErrors(error, 'group');
        setApiValidationError(errors);
      } else {
        addErrorToast(error?.message);
      }
    }
  };

  async function deleteGroup() {
    try {
      setIsPopupShown(false);
      await deleteGroupById(id);
      history.push(routes.groups());
      addSuccessToast(t('toasts.removeGroupMessage'));
    } catch (error) {
      addErrorToast(error?.message);
    }
  }

  function showConfirmationPopup() {
    setIsPopupShown(true);
  }

  return (
    <div className="tab-content">
      <Formik
        initialValues={initialValues}
        validationSchema={editGroupValidationSchema}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {({setFieldTouched, submitForm,
          isSubmitting, isValid, dirty}) => {
          useOnUnsavedChanges(dirty);

          return (
            <>
              <Form className="form">
                <FormGroup>
                  <TextField
                    label="Group Name"
                    id="groupName"
                    name="name"
                    required
                    onBlur={() => {
                      setFieldTouched('name', true);
                      clearMessages();
                    }}
                    apiValidationError={apiValidationError}
                  />
                </FormGroup>
              </Form>

              <div className="buttons">
                <EnsurePermission
                  elementPermissions={permissionsInfo.groups.edit}>
                  <ButtonWithLoader
                    onClick={submitForm}
                    disabled={!(isValid && dirty)}
                    isSubmitting={isSubmitting}
                    label="Update group"
                    type="submit"
                  />
                </EnsurePermission>

                {apiData?.slug === sysAdminGroupSlug ? null :
                <>
                  <ConfirmationPopup
                    labels={popupLabels}
                    isOpen={isPopupShown}
                    setIsOpen={setIsPopupShown}
                    confirmableAction={deleteGroup}
                  />
                  <EnsurePermission
                    elementPermissions={permissionsInfo.groups.edit}>
                    <Button onClick={showConfirmationPopup}
                      label="Delete group"
                      variant="text"
                      color="secondary"
                    />
                  </EnsurePermission>
                </>
                }
              </div>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default Information;
